import React from 'react'
import PropTypes from 'prop-types'
import { Stack, Button } from '@mui/material'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import MosSmartDialog from './MosSmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'

/**
 * Story 113 (S124-S126, RN41) — the modal behind the "Change status" button that replaces Pause on a
 * job in execution that manages preparation.
 *
 * EXACTLY TWO actions and no third one: go back to preparation, or pause the work. Closing it without
 * choosing sends nothing and leaves the job exactly as it was (S125) -- which is why each action is a
 * button of its own rather than a selection plus a confirmation: there is no "accepted but empty"
 * state to reason about.
 *
 * The dialog itself dispatches nothing. Both callbacks are the widget's own existing flows (the mode
 * transition and today's pause), so this component adds no request path of its own.
 */
const JobModeChangeDialog = ({ open, onClose, onBackToPreparation, onPauseWork }) => {
  const { t_ } = useSmartTranslation()

  if (!open) return null

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Change status'), icon: SwapHorizRoundedIcon })}
      setOpen={open}
      cancelCallback={onClose}
      accept={false}
    >
      <Stack spacing={2} sx={{ p: (theme) => theme.spacing(3) }}>
        <Button
          variant='outlined'
          color='warning'
          startIcon={<BuildOutlinedIcon />}
          sx={{ textTransform: 'none', height: 56, fontSize: '1.1rem' }}
          onClick={onBackToPreparation}
        >
          {t_('Back to preparation')}
        </Button>
        <Button
          variant='outlined'
          color='inherit'
          startIcon={<PauseCircleOutlineIcon />}
          sx={{ textTransform: 'none', height: 56, fontSize: '1.1rem' }}
          onClick={onPauseWork}
        >
          {t_('Pause work')}
        </Button>
      </Stack>
    </MosSmartDialog>
  )
}

JobModeChangeDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onBackToPreparation: PropTypes.func.isRequired,
  onPauseWork: PropTypes.func.isRequired
}

export default JobModeChangeDialog
